import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import "./SubNav.css";

/**
 * SubNav component — the top sub navigation bar for a section of the app.
 * Designed to be passed to PageContent's `customHeader`, where it bleeds
 * edge-to-edge and lines up with the sidebar header.
 *
 * @param {Object} props - Component props
 * @param {Array<{id: string, label: string, icon?: React.ElementType, path?: string, disabled?: boolean}>} props.tabs - Tabs to render; `icon` is the component itself (e.g. IconChartBar), which the bar renders at its own size so every tab matches
 * @param {React.ReactNode} props.title - Section title shown at the bar's left edge, with the tabs following it (optional)
 * @param {string} props.activeId - Id of the currently active tab
 * @param {Function} props.onChange - Called with the clicked tab; when omitted, the tab's `path` is navigated to
 * @param {React.ReactNode} props.actions - Optional right-aligned content (buttons, filters, …)
 * @param {string} props.className - Additional CSS classes (optional)
 * @param {string} props.ariaLabel - Accessible name for the tab strip (optional)
 * @returns {JSX.Element} The SubNav component
 */
function SubNav({
  tabs = [],
  title = null,
  activeId,
  onChange = null,
  actions = null,
  className = "",
  ariaLabel = "Section navigation",
}) {
  const navigate = useNavigate();

  const handleSelect = tab => {
    if (tab.disabled) {
      return;
    }

    if (onChange) {
      onChange(tab);
    } else if (tab.path) {
      navigate(tab.path);
    }
  };

  return (
    <div className={`sub-nav-bar ${className}`.trim()}>
      {title && <h1 className="sub-nav-title">{title}</h1>}
      <nav className="sub-nav-tabs" role="tablist" aria-label={ariaLabel}>
        {tabs.map(tab => {
          const isActive = tab.id === activeId;
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-current={isActive ? "page" : undefined}
              className={`sub-nav-tab${isActive ? " active" : ""}`}
              onClick={() => handleSelect(tab)}
              disabled={tab.disabled}
              data-testid={`sub-nav-tab-${tab.id}`}
            >
              {Icon && (
                <Icon
                  size={16}
                  stroke={1.75}
                  className="sub-nav-tab-icon"
                  aria-hidden="true"
                />
              )}
              {tab.label}
            </button>
          );
        })}
      </nav>
      {actions && <div className="sub-nav-actions">{actions}</div>}
    </div>
  );
}

SubNav.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.elementType,
      path: PropTypes.string,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  title: PropTypes.node,
  activeId: PropTypes.string,
  onChange: PropTypes.func,
  actions: PropTypes.node,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

export default SubNav;
